import {useState, type FormEvent} from 'react';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';

interface Props {
    name: string;
    isRenaming: boolean;
    error: string;
    onClose: () => void;
    onRename: (name: string) => void;
}

/** Mounted for each rename so the input starts with the latest saved name. */
export default function AutomationRenameDialog({name, isRenaming, error, onClose, onRename}: Props) {
    const {t} = useTranslation();
    const [draft, setDraft] = useState(name);
    const trimmedName = draft.trim();
    const canRename = !!trimmedName && trimmedName !== name && !isRenaming;

    return <Dialog open fullWidth aria-label={t('Rename automation')} onClose={() => {if (!isRenaming) onClose();}}
        disableEscapeKeyDown={isRenaming}
        PaperProps={{component: 'form', sx: {maxWidth: 480}, onSubmit: (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (canRename) onRename(trimmedName);
        }}}>
        <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                {t('Rename automation')}
                <Tooltip title={t('Close')}>
                    <IconButton size="small" aria-label={t('Close')} disabled={isRenaming} onClick={onClose}>
                        <CloseIcon/>
                    </IconButton>
                </Tooltip>
            </Stack>
        </DialogTitle>
        <DialogContent>
            <TextField autoFocus fullWidth required size="small" margin="dense" label={t('Automation Name')}
                value={draft} disabled={isRenaming} onChange={event => setDraft(event.target.value)}
                onFocus={event => event.target.select()}
                error={!trimmedName} helperText={!trimmedName ? t('Name is required') : undefined}/>
            {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
            <Button size="small" disabled={isRenaming} onClick={onClose}>{t('Cancel')}</Button>
            <LoadingButton size="small" type="submit" variant="contained" loading={isRenaming} disabled={!canRename}>
                {t('Rename')}
            </LoadingButton>
        </DialogActions>
    </Dialog>;
}
